import React, { useState, useEffect } from 'react';
import { configAPI } from '../../lib/api';
import { GlassCard } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Settings, Save } from 'lucide-react';

const AdminConfig = () => {
  const [config, setConfig] = useState({
    escalation_minutes: null,
    escalation_hours: 24,
    rank_criteria: {
      rm: 5,
      avp: 15,
      svp: 30,
      evp: 50,
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await configAPI.getConfig();
      // Backward-compat: server may return either escalation_minutes (V4.18+)
      // or the legacy escalation_hours. Surface BOTH so admins can switch
      // between the two without re-typing the other.
      setConfig({
        escalation_minutes: res.data?.escalation_minutes ?? null,
        escalation_hours: res.data?.escalation_hours ?? null,
        rank_criteria: res.data?.rank_criteria || { rm: 5, avp: 15, svp: 30, evp: 50 },
      });
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build the payload. Only include fields the admin actually filled in
      // so the backend can decide what to $set vs $unset. The new
      // PUT /api/config handler unsets `escalation_hours` automatically when
      // `escalation_minutes` is supplied.
      const payload = {
        rank_criteria: Object.fromEntries(
          Object.entries(config.rank_criteria).map(([k, v]) => [k, parseInt(v, 10) || 0])
        ),
      };
      const minutes = config.escalation_minutes;
      const hours = config.escalation_hours;
      if (minutes !== null && minutes !== '' && minutes !== undefined) {
        payload.escalation_minutes = parseInt(minutes, 10);
      } else if (hours !== null && hours !== '' && hours !== undefined) {
        payload.escalation_hours = parseInt(hours, 10);
      }
      await configAPI.updateConfig(payload);
      toast.success('Configuration saved');
      // Re-pull so the UI reflects any server-side normalisation (e.g.
      // escalation_hours getting unset when minutes is set).
      await loadConfig();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to save configuration';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-config">
      <div>
        <h1 className="text-2xl font-bold text-white">System Configuration</h1>
        <p className="text-slate-400 mt-1">Configure system-wide settings</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Escalation Settings */}
        <GlassCard>
          <div className="flex items-center gap-3 mb-6">
            <Settings className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Appointment Escalation</h2>
          </div>
          <div className="space-y-5">
            <div>
              <Label className="text-slate-300">Escalation Time (Minutes)</Label>
              <Input
                type="number"
                min="1"
                value={config.escalation_minutes ?? ''}
                onChange={(e) => setConfig({ ...config, escalation_minutes: e.target.value === '' ? null : e.target.value })}
                placeholder="e.g. 2 (testing) or 120 (2 hours)"
                className="bg-slate-800/50 border-slate-700 text-white mt-2 max-w-xs"
                data-testid="escalation-minutes"
              />
              <p className="text-xs text-slate-500 mt-2">
                Preferred unit. Takes precedence over Hours when set. Leave blank to fall back to Hours.
              </p>
            </div>
            <div>
              <Label className="text-slate-300">Escalation Time (Hours) — Legacy</Label>
              <Input
                type="number"
                min="1"
                value={config.escalation_hours ?? ''}
                onChange={(e) => setConfig({ ...config, escalation_hours: e.target.value === '' ? null : e.target.value })}
                placeholder="e.g. 24"
                disabled={config.escalation_minutes !== null && config.escalation_minutes !== ''}
                className="bg-slate-800/50 border-slate-700 text-white mt-2 max-w-xs disabled:opacity-40"
                data-testid="escalation-hours"
              />
              <p className="text-xs text-slate-500 mt-2">
                Used only when Minutes is empty. Saving Minutes clears this automatically.
              </p>
            </div>
            <p className="text-xs text-emerald-400/80 bg-emerald-500/10 border border-emerald-500/20 rounded px-3 py-2">
              Appointments not actioned within this window are escalated up the agent chain
              (NEG → RM → AVP → SVP → EVP).
            </p>
          </div>
        </GlassCard>

        {/* Rank Criteria */}
        <GlassCard>
          <div className="flex items-center gap-3 mb-6">
            <Settings className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">Auto-Ranking Criteria</h2>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Relationship Manager (RM)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  value={config.rank_criteria?.rm || 5}
                  onChange={(e) => setConfig({
                    ...config,
                    rank_criteria: { ...config.rank_criteria, rm: e.target.value }
                  })}
                  className="bg-slate-800/50 border-slate-700 text-white max-w-xs"
                />
                <span className="text-slate-400 text-sm">completed sales</span>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Assistant Vice President (AVP)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  value={config.rank_criteria?.avp || 15}
                  onChange={(e) => setConfig({
                    ...config,
                    rank_criteria: { ...config.rank_criteria, avp: e.target.value }
                  })}
                  className="bg-slate-800/50 border-slate-700 text-white max-w-xs"
                />
                <span className="text-slate-400 text-sm">completed sales</span>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Senior Vice President (SVP)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  value={config.rank_criteria?.svp || 30}
                  onChange={(e) => setConfig({
                    ...config,
                    rank_criteria: { ...config.rank_criteria, svp: e.target.value }
                  })}
                  className="bg-slate-800/50 border-slate-700 text-white max-w-xs"
                />
                <span className="text-slate-400 text-sm">completed sales</span>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Executive Vice President (EVP)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  value={config.rank_criteria?.evp || 50}
                  onChange={(e) => setConfig({
                    ...config,
                    rank_criteria: { ...config.rank_criteria, evp: e.target.value }
                  })}
                  className="bg-slate-800/50 border-slate-700 text-white max-w-xs"
                />
                <span className="text-slate-400 text-sm">completed sales</span>
              </div>
            </div>
          </div>
        </GlassCard>
      </div>

      <Button
        onClick={handleSave}
        disabled={saving}
        className="bg-emerald-500 hover:bg-emerald-600"
        data-testid="save-config"
      >
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save Configuration'}
      </Button>
    </div>
  );
};

export default AdminConfig;
